import type { Href } from "expo-router";
import { router } from "expo-router";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Platform, Pressable, ScrollView, Text, View, type ViewStyle } from "react-native";

import { COLORS, FONTS, RADII, STROKES } from "../../../lib/brand";
import { PANE_THEMES } from "../../../components/brand/paneThemes";
import { useAuth } from "../../../lib/authContext";
import { enterImpersonation } from "../../../lib/impersonate";
import { formatUsdCompact } from "../../../lib/winners";
import {
  getAnalyticsFunnel,
  getAnalyticsOverview,
  getRecentSignups,
  getSignupSeries,
  pctChange,
  type AnalyticsFunnel,
  type AnalyticsOverview,
  type RecentSignup,
  type SignupPoint,
} from "../../../lib/adminAnalytics";
import { StatStrip, type StatStripCell } from "../../../components/aspects/StatStrip";
import { AdminButton, AdminEmpty, AdminLoading, AdminPageHeader } from "../../../components/admin/ui";

const THEME = PANE_THEMES.Admin;

// Per-role series colors — brand tokens only.
const ROLE_META: { key: string; label: string; color: string }[] = [
  { key: "student", label: "Students", color: COLORS.AMBER.hex },
  { key: "client", label: "Clients", color: COLORS.SAGE.hex },
  { key: "judge", label: "Judges", color: COLORS.LIVE_GREEN.hex },
  { key: "admin", label: "Admins", color: COLORS.GOLD_RM.hex },
  { key: "other", label: "Other", color: COLORS.SAGE_DEEP.hex },
];
const COLOR_FOR = (role: string) => ROLE_META.find((r) => r.key === role)?.color ?? COLORS.SAGE_DEEP.hex;

type Range = "7d" | "30d" | "90d" | "all";
const RANGES: { key: Range; label: string; days: number | null; bucket: "day" | "week" }[] = [
  { key: "7d", label: "7d", days: 7, bucket: "day" },
  { key: "30d", label: "30d", days: 30, bucket: "day" },
  { key: "90d", label: "90d", days: 90, bucket: "week" },
  { key: "all", label: "All", days: null, bucket: "week" },
];

export default function AdminAnalyticsScreen() {
  const { role } = useAuth();
  const isSuperAdmin = role === "super_admin";

  const [overview, setOverview] = useState<AnalyticsOverview | null>(null);
  const [series, setSeries] = useState<SignupPoint[]>([]);
  const [recent, setRecent] = useState<RecentSignup[]>([]);
  const [funnel, setFunnel] = useState<AnalyticsFunnel | null>(null);
  const [range, setRange] = useState<Range>("30d");
  const [loading, setLoading] = useState(true);

  const rangeCfg = RANGES.find((r) => r.key === range)!;

  const load = useCallback(async () => {
    setLoading(true);
    const [ov, fn, rc] = await Promise.all([
      getAnalyticsOverview(),
      getAnalyticsFunnel(),
      getRecentSignups(40),
    ]);
    setOverview(ov);
    setFunnel(fn);
    setRecent(rc);
    setLoading(false);
  }, []);

  // Signups series reloads whenever the range changes.
  useEffect(() => {
    let cancelled = false;
    void getSignupSeries(rangeCfg.days, rangeCfg.bucket).then((pts) => {
      if (!cancelled) setSeries(pts);
    });
    return () => {
      cancelled = true;
    };
  }, [rangeCfg.days, rangeCfg.bucket]);

  useEffect(() => {
    void load();
  }, [load]);

  if (!isSuperAdmin) {
    return (
      <View style={{ flex: 1, backgroundColor: THEME.pageBg, padding: 24 }}>
        <AdminEmpty theme={THEME} title="Super admin only" body="Analytics is restricted to super admins." />
      </View>
    );
  }

  return (
    <ScrollView style={{ flex: 1, backgroundColor: THEME.pageBg }} contentContainerStyle={{ padding: 24, gap: 18 }}>
      <AdminPageHeader
        theme={THEME}
        eyebrow="Super admin"
        title="Analytics"
        subtitle="Signups, users by role, who's active recently, and drill-in."
        actions={<AdminButton theme={THEME} label="Refresh" variant="outline" size="sm" onPress={() => void load()} />}
      />

      {loading && !overview ? (
        <AdminLoading theme={THEME} label="Loading analytics…" />
      ) : !overview ? (
        <AdminEmpty theme={THEME} title="No data" body="Couldn't load analytics right now." />
      ) : (
        <>
          {/* Tiles */}
          <StatStrip theme={THEME} eyebrow="Users" cells={userCells(overview)} />
          <StatStrip theme={THEME} eyebrow="New signups" cells={signupCells(overview)} />
          <StatStrip theme={THEME} eyebrow="Active recently" cells={activeCells(overview)} />
          <StatStrip theme={THEME} eyebrow="Live now" cells={liveCells(overview)} />

          {/* Signups chart */}
          <View
            style={{
              backgroundColor: THEME.surfaceRaisedBg,
              borderRadius: RADII.md,
              borderWidth: STROKES.hairline,
              borderColor: THEME.border,
              padding: 18,
              gap: 14,
            }}
          >
            <View style={{ flexDirection: "row", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
              <Text style={{ fontFamily: FONTS.bodyBold, fontSize: 9.5, letterSpacing: 1.9, textTransform: "uppercase", color: THEME.textOnSurfaceFaint }}>
                Signups over time
              </Text>
              <View style={{ flexDirection: "row", gap: 6 }}>
                {RANGES.map((r) => (
                  <RangePill key={r.key} label={r.label} active={range === r.key} onPress={() => setRange(r.key)} />
                ))}
              </View>
            </View>
            <SignupsChart points={series} weekly={rangeCfg.bucket === "week"} />
            <Legend />
          </View>

          {/* Funnel */}
          {funnel ? <FunnelPanel funnel={funnel} /> : null}

          {/* Recent signups feed */}
          <View style={{ gap: 8 }}>
            <Text style={{ fontFamily: FONTS.bodyBold, fontSize: 9.5, letterSpacing: 1.9, textTransform: "uppercase", color: THEME.textOnSurfaceFaint }}>
              Recent signups
            </Text>
            <View
              style={{
                backgroundColor: THEME.surfaceRaisedBg,
                borderRadius: RADII.md,
                borderWidth: STROKES.hairline,
                borderColor: THEME.border,
                overflow: "hidden",
              }}
            >
              {recent.length === 0 ? (
                <View style={{ padding: 16 }}>
                  <Text style={{ fontFamily: FONTS.body, fontSize: 12.5, color: THEME.textOnSurfaceFaint }}>No recent signups.</Text>
                </View>
              ) : (
                recent.map((u, i) => <SignupRow key={u.id} u={u} last={i === recent.length - 1} />)
              )}
            </View>
          </View>
        </>
      )}
    </ScrollView>
  );
}

/* ── Tile builders ── */

function userCells(o: AnalyticsOverview): StatStripCell[] {
  return [
    { label: "Total", value: o.total_users },
    { label: "Students", value: o.by_role.student, color: COLOR_FOR("student") },
    { label: "Clients", value: o.by_role.client, color: COLOR_FOR("client") },
    { label: "Judges", value: o.by_role.judge, color: COLOR_FOR("judge") },
    { label: "Admins", value: o.by_role.admin, color: COLOR_FOR("admin") },
  ];
}

function deltaLabel(base: string, current: number, prior: number): string {
  const pct = pctChange(current, prior);
  if (pct === null) return base;
  const arrow = pct > 0 ? "▲" : pct < 0 ? "▼" : "■";
  return `${base}  ${arrow} ${Math.abs(pct)}%`;
}

function signupCells(o: AnalyticsOverview): StatStripCell[] {
  return [
    { label: "Today", value: o.signups.today },
    { label: deltaLabel("Last 7 days", o.signups.d7, o.signups.prev7), value: o.signups.d7 },
    { label: deltaLabel("Last 30 days", o.signups.d30, o.signups.prev30), value: o.signups.d30 },
  ];
}

function activeCells(o: AnalyticsOverview): StatStripCell[] {
  return [
    { label: "Last 5 min", value: o.active.m5, color: COLORS.LIVE_GREEN.hex },
    { label: "Last 30 min", value: o.active.m30 },
    { label: "Last 60 min", value: o.active.m60 },
    { label: "Last 24h", value: o.active.h24 },
  ];
}

function liveCells(o: AnalyticsOverview): StatStripCell[] {
  return [
    { label: "Active cases", value: o.active_cases },
    { label: "Scholarship live", value: formatUsdCompact(o.scholarship_live_cents), color: COLORS.AMBER.hex },
  ];
}

/* ── Chart ── */

function SignupsChart({ points, weekly }: { points: SignupPoint[]; weekly: boolean }) {
  const { buckets, max } = useMemo(() => {
    const byBucket = new Map<string, Record<string, number>>();
    for (const p of points) {
      const b = byBucket.get(p.bucket) ?? {};
      b[p.role] = (b[p.role] ?? 0) + p.n;
      byBucket.set(p.bucket, b);
    }
    const keys = [...byBucket.keys()].sort();
    let m = 0;
    for (const k of keys) {
      const total = Object.values(byBucket.get(k)!).reduce((s, n) => s + n, 0);
      if (total > m) m = total;
    }
    return { buckets: keys.map((k) => ({ key: k, roles: byBucket.get(k)! })), max: m || 1 };
  }, [points]);

  const CHART_H = 160;

  if (buckets.length === 0) {
    return (
      <View style={{ height: CHART_H, alignItems: "center", justifyContent: "center" }}>
        <Text style={{ fontFamily: FONTS.body, fontSize: 12.5, color: THEME.textOnSurfaceFaint }}>
          No signups in this range.
        </Text>
      </View>
    );
  }

  return (
    <ScrollView horizontal showsHorizontalScrollIndicator style={{ marginHorizontal: -4 }}>
      <View style={{ flexDirection: "row", alignItems: "flex-end", height: CHART_H, gap: 4, paddingHorizontal: 4 }}>
        {buckets.map((b) => {
          const total = ROLE_META.reduce((s, r) => s + (b.roles[r.key] ?? 0), 0);
          const barH = Math.max(2, Math.round((total / max) * (CHART_H - 24)));
          return (
            <View key={b.key} style={{ alignItems: "center", justifyContent: "flex-end", width: 26 }}>
              <View style={{ height: barH, width: 16, borderRadius: 3, overflow: "hidden", flexDirection: "column-reverse" }}>
                {ROLE_META.map((r) => {
                  const n = b.roles[r.key] ?? 0;
                  if (n <= 0) return null;
                  return <View key={r.key} style={{ height: Math.max(1, (n / total) * barH), backgroundColor: r.color }} />;
                })}
              </View>
              <Text style={{ marginTop: 5, fontFamily: FONTS.body, fontSize: 8, color: THEME.textOnSurfaceFaint }} numberOfLines={1}>
                {shortBucket(b.key, weekly)}
              </Text>
            </View>
          );
        })}
      </View>
    </ScrollView>
  );
}

function Legend() {
  return (
    <View style={{ flexDirection: "row", flexWrap: "wrap", gap: 14 }}>
      {ROLE_META.filter((r) => r.key !== "other").map((r) => (
        <View key={r.key} style={{ flexDirection: "row", alignItems: "center", gap: 6 }}>
          <View style={{ width: 8, height: 8, borderRadius: 2, backgroundColor: r.color }} />
          <Text style={{ fontFamily: FONTS.bodyMedium, fontSize: 11, color: THEME.textOnSurfaceDim }}>{r.label}</Text>
        </View>
      ))}
    </View>
  );
}

/* ── Funnel ── */

function FunnelPanel({ funnel }: { funnel: AnalyticsFunnel }) {
  const rows: { label: string; value: number; of: number }[] = [
    { label: "Students signed up", value: funnel.students_total, of: funnel.students_total },
    { label: "Registered for a case", value: funnel.students_registered, of: funnel.students_total },
    { label: "Submitted a deliverable", value: funnel.students_submitted, of: funnel.students_total },
  ];
  return (
    <View
      style={{
        backgroundColor: THEME.surfaceRaisedBg,
        borderRadius: RADII.md,
        borderWidth: STROKES.hairline,
        borderColor: THEME.border,
        padding: 18,
        gap: 12,
      }}
    >
      <Text style={{ fontFamily: FONTS.bodyBold, fontSize: 9.5, letterSpacing: 1.9, textTransform: "uppercase", color: THEME.textOnSurfaceFaint }}>
        Student funnel
      </Text>
      {rows.map((r) => {
        const pct = r.of > 0 ? Math.round((r.value / r.of) * 100) : 0;
        return (
          <View key={r.label} style={{ gap: 4 }}>
            <View style={{ flexDirection: "row", justifyContent: "space-between" }}>
              <Text style={{ fontFamily: FONTS.bodyMedium, fontSize: 12.5, color: THEME.textOnSurface }}>{r.label}</Text>
              <Text style={{ fontFamily: FONTS.bodySemiBold, fontSize: 12.5, color: THEME.textOnSurfaceDim }}>
                {r.value} · {pct}%
              </Text>
            </View>
            <View style={{ height: 6, borderRadius: 3, backgroundColor: THEME.surfaceBg, overflow: "hidden" }}>
              <View style={{ height: 6, width: `${pct}%`, backgroundColor: THEME.accent }} />
            </View>
          </View>
        );
      })}
    </View>
  );
}

/* ── Recent signup row ── */

function detailHref(u: RecentSignup): Href {
  if (u.role === "student") return `/admin/student/${u.id}` as Href;
  if (u.role === "judge") return `/admin/judge/${u.id}` as Href;
  return "/admin/users" as Href;
}

function SignupRow({ u, last }: { u: RecentSignup; last: boolean }) {
  const isWeb = Platform.OS === "web";
  const name = u.full_name?.trim() || u.email || u.id.slice(0, 8);
  const meta = ROLE_META.find((r) => r.key === u.role);
  return (
    <Pressable
      onPress={() => router.push(detailHref(u))}
      {...(isWeb ? ({} as const) : {})}
      style={({ hovered }: { hovered?: boolean }) => [
        {
          flexDirection: "row",
          alignItems: "center",
          gap: 12,
          paddingHorizontal: 16,
          paddingVertical: 11,
          borderBottomWidth: last ? 0 : STROKES.hairline,
          borderBottomColor: THEME.divider,
          backgroundColor: hovered && isWeb ? THEME.neutralHoverBgOnDark : "transparent",
        } as ViewStyle,
        isWeb ? ({ cursor: "pointer" } as const) : null,
      ]}
    >
      <View style={{ flex: 1, minWidth: 0 }}>
        <Text numberOfLines={1} style={{ fontFamily: FONTS.bodySemiBold, fontSize: 13, color: THEME.textOnSurface }}>
          {name}
        </Text>
        <Text numberOfLines={1} style={{ marginTop: 1, fontFamily: FONTS.body, fontSize: 11.5, color: THEME.textOnSurfaceDim }}>
          {u.email ?? "—"}
        </Text>
      </View>
      <View style={{ paddingHorizontal: 7, paddingVertical: 2, borderRadius: RADII.full, borderWidth: STROKES.hairline, borderColor: meta?.color ?? THEME.border }}>
        <Text style={{ fontFamily: FONTS.bodyBold, fontSize: 8.5, letterSpacing: 0.8, textTransform: "uppercase", color: meta?.color ?? THEME.textOnSurfaceDim }}>
          {u.role}
        </Text>
      </View>
      <Text style={{ width: 64, textAlign: "right", fontFamily: FONTS.body, fontSize: 10.5, color: THEME.textOnSurfaceFaint }}>
        {timeAgo(u.created_at)}
      </Text>
      <View style={{ width: 8, height: 8, borderRadius: 4, backgroundColor: u.verified ? COLORS.LIVE_GREEN.hex : THEME.textOnSurfaceFaint }} />
      <Pressable
        onPress={(e) => {
          e.stopPropagation?.();
          void enterImpersonation({ userId: u.id, email: u.email });
        }}
        style={[
          { paddingHorizontal: 8, paddingVertical: 4, borderRadius: RADII.full, borderWidth: STROKES.hairline, borderColor: THEME.accentBorderSoft },
          isWeb ? ({ cursor: "pointer" } as const) : null,
        ]}
      >
        <Text style={{ fontFamily: FONTS.bodyBold, fontSize: 9, letterSpacing: 0.6, textTransform: "uppercase", color: THEME.accent }}>
          View as →
        </Text>
      </Pressable>
    </Pressable>
  );
}

function RangePill({ label, active, onPress }: { label: string; active: boolean; onPress: () => void }) {
  const isWeb = Platform.OS === "web";
  return (
    <Pressable
      onPress={onPress}
      style={[
        {
          paddingHorizontal: 10,
          paddingVertical: 4,
          borderRadius: RADII.full,
          borderWidth: STROKES.hairline,
          borderColor: active ? THEME.accent : THEME.border,
          backgroundColor: active ? THEME.accentTintSoft : "transparent",
        } as ViewStyle,
        isWeb ? ({ cursor: "pointer" } as const) : null,
      ]}
    >
      <Text style={{ fontFamily: FONTS.bodyBold, fontSize: 10.5, color: active ? THEME.accent : THEME.textOnSurfaceDim }}>
        {label}
      </Text>
    </Pressable>
  );
}

/* ── helpers ── */

function shortBucket(iso: string, weekly: boolean): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const m = d.getMonth() + 1;
  const day = d.getDate();
  return weekly ? `${m}/${day}` : `${m}/${day}`;
}

function timeAgo(iso: string): string {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "";
  const mins = Math.floor((Date.now() - then) / 60000);
  if (mins < 1) return "now";
  if (mins < 60) return `${mins}m`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `${days}d`;
  const mo = Math.floor(days / 30);
  return `${mo}mo`;
}
